import { formatInteger, formatPercent } from '@/design'
import { formatRevenue } from '@/features/analytics/traffic/metrics'

/** What `ChannelBarChart` can plot; also the wire values for the `tr_chart`/`ov_chart` URL params. */
export const CHART_METRICS = ['revenue', 'orders', 'conversion'] as const
export type ChartMetric = (typeof CHART_METRICS)[number]

export const METRIC_OPTIONS: readonly { value: ChartMetric; label: string }[] = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'orders', label: 'Orders' },
  { value: 'conversion', label: 'Conversion' },
]

/**
 * One channel's figures, in the shape `ChannelBarChart` plots — the one data shape
 * shared by the Traffic page's first-party report and the Overview's channel
 * widget, each adapted into it at their own call site.
 */
export interface ChannelBarRow {
  key: string
  label: string
  revenueMinor: number
  orders: number
  /** `orders / sessions`; null when there were no sessions — shown as "—", never zero. */
  conversionRate: number | null
  /** Shown in the bar's hover title alongside orders; not plotted. */
  sessions: number
  /** The Not tracked row: orders with no tracked visit. Drawn neutral, always last, never merged with Direct. */
  notTracked?: boolean
}

const FIELD = { revenue: 'revenueMinor', orders: 'orders', conversion: 'conversionRate' } as const

export function channelBarValue(row: ChannelBarRow, metric: ChartMetric): number | null {
  return row[FIELD[metric]]
}

/**
 * The minor-to-major currency conversion is `formatRevenue`'s (traffic's own
 * first-party and GA4 tables use it for the same figures) — reused here rather
 * than re-derived, so a currency-specific rounding fix can never apply to the
 * table but not the chart on the same page.
 */
export function formatChannelBarValue(metric: ChartMetric, value: number | null, currency: string): string {
  if (value === null) return '—'
  if (metric === 'revenue') return formatRevenue(value, currency)
  if (metric === 'conversion') return formatPercent(value)
  return formatInteger(value)
}
